import { FC, useState } from "react";
import { AddMealInsightFeedbackRequest } from "@9amhealth/openapi";
import {
  IconThumbsDown,
  IconThumbsDownFilled,
  IconThumbsUp,
  IconThumbsUpFilled
} from "src/constants/icons";
import styled from "@emotion/styled";

const FeedbackSection = styled.div`
  padding: 0;
`;

const FeedbackButtons = styled.div`
  display: flex;
  justify-content: left;
  gap: 0.125rem;
`;

const FeedbackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  color: var(--color-charcoal, #212121);
  transition: background-color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 2px solid #6941c6;
    outline-offset: 2px;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  span {
    font-size: 0.75rem;
  }
`;

interface FeedbackProps {
  initialRating?: AddMealInsightFeedbackRequest.rating | null;
  onRate: (rating: AddMealInsightFeedbackRequest.rating) => void;
}

const Feedback: FC<FeedbackProps> = ({ initialRating = null, onRate }) => {
  const [feedback, setFeedback] =
    useState<AddMealInsightFeedbackRequest.rating | null>(initialRating);

  const handleFeedback = (rating: AddMealInsightFeedbackRequest.rating) => {
    setFeedback(rating);
    onRate(rating);
  };

  const goodFeedback = AddMealInsightFeedbackRequest.rating.GOOD;
  const badFeedback = AddMealInsightFeedbackRequest.rating.BAD;
  const removedFeedback = AddMealInsightFeedbackRequest.rating.REMOVED;

  return (
    <FeedbackSection>
      <div role="group" aria-label="Rate this meal insight">
        <FeedbackButtons>
          <FeedbackButton
            onClick={() =>
              handleFeedback(
                feedback === goodFeedback ? removedFeedback : goodFeedback
              )
            }
            aria-label={
              feedback === goodFeedback
                ? "Remove helpful rating"
                : "Rate as helpful"
            }
            aria-pressed={feedback === goodFeedback}
          >
            {feedback === goodFeedback ? (
              <IconThumbsUpFilled aria-hidden="true" />
            ) : (
              <IconThumbsUp aria-hidden="true" />
            )}
            <span className="only-sr">
              {feedback === goodFeedback
                ? "Rated as helpful"
                : "Rate as helpful"}
            </span>
          </FeedbackButton>

          <FeedbackButton
            onClick={() =>
              handleFeedback(
                feedback === badFeedback ? removedFeedback : badFeedback
              )
            }
            aria-label={
              feedback === badFeedback
                ? "Remove not helpful rating"
                : "Rate as not helpful"
            }
            aria-pressed={feedback === badFeedback}
          >
            {feedback === badFeedback ? (
              <IconThumbsDownFilled aria-hidden="true" />
            ) : (
              <IconThumbsDown aria-hidden="true" />
            )}
            <span className="only-sr">
              {feedback === badFeedback
                ? "Rated as not helpful"
                : "Rate as not helpful"}
            </span>
          </FeedbackButton>
        </FeedbackButtons>
      </div>
    </FeedbackSection>
  );
};

export default Feedback;
